import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Bot, TrendingUp, ArrowRight, Edit2 } from 'lucide-react';
import api from '../services/api';
import UpdateTokenImage from './UpdateTokenImage';

// Cache for token data to improve performance
const tokenDataCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

function IPTokenSelection() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [availableTokens, setAvailableTokens] = useState([]);
  const [authorizedTokens, setAuthorizedTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tokensLoaded, setTokensLoaded] = useState(false); // Show tokens immediately
  const [editingToken, setEditingToken] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [tokenBalances, setTokenBalances] = useState({});
  const [tokenSummaries, setTokenSummaries] = useState({});
  const [balancesLoading, setBalancesLoading] = useState({});


  useEffect(() => {
    fetchAvailableTokens();
    fetchAuthorizedTokens();
  }, []);

  // Check if cache is valid
  const isCacheValid = (cacheData) => {
    return cacheData && (Date.now() - cacheData.timestamp) < CACHE_DURATION;
  };

  // Fetch Total Portfolio Value and 24h Change for each authorized token using asset overview
  const fetchTokenBalances = async (tokens) => {
    const balances = {};
    const summaries = {};

    // Set loading state for all tokens
    const loadingState = {};
    tokens.forEach(token => {
      loadingState[token.id] = true;
    });
    setBalancesLoading(loadingState);

    // Process all tokens in parallel with caching and timeout
    const tokenPromises = tokens.map(async (token) => {
      try {
        // Check cache first
        const cacheKey = `token_${token.id}`;
        const cachedData = tokenDataCache.get(cacheKey);

        if (isCacheValid(cachedData)) {
          console.log(`Using cached data for token ${token.id}`);
          // Use cached data
          setTokenBalances(prev => ({ ...prev, [token.id]: cachedData.balance }));
          setTokenSummaries(prev => ({ ...prev, [token.id]: cachedData.summary }));
          setBalancesLoading(prev => ({ ...prev, [token.id]: false }));
          return;
        }

        // Create promises with timeout handling
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout')), 8000) // 8 second timeout
        );

        // Use token_symbol for API calls instead of numeric id
        const tokenIdentifier = token.token_symbol;

        // Fetch both asset overview and historical data in parallel with timeout
        const [assetResponse, historicalResponse] = await Promise.allSettled([
          Promise.race([api.get(`/asset-overview/${tokenIdentifier}`), timeoutPromise]),
          Promise.race([api.get(`/asset-historical/${tokenIdentifier}`), timeoutPromise])
        ]);
        
        // Handle asset overview response
        if (assetResponse.status === 'fulfilled') {
          const assetData = assetResponse.value.data;

          // Use pre-calculated total from backend (either from cache or real-time calculation)
          const actualTotal = parseFloat(assetData.totalValueUsd) || 0;

          balances[token.id] = actualTotal;

          // Update balance immediately for this token (progressive loading)
          setTokenBalances(prev => ({ ...prev, [token.id]: actualTotal }));
          setBalancesLoading(prev => ({ ...prev, [token.id]: false }));

          // Log cache status for debugging
          if (assetData.is_cached) {
            console.log(`✅ Using cached data for token ${token.token_symbol}: $${actualTotal.toFixed(2)} (cached at: ${assetData.last_updated})`);
          } else {
            console.log(`🔄 Using real-time data for token ${token.token_symbol}: $${actualTotal.toFixed(2)}`);
          }

          // Handle historical data response
          if (historicalResponse.status === 'fulfilled') {
            const historical = historicalResponse.value.data;

            if (historical && historical.history) {
              const value24hAgo = historical.history['24h'] || actualTotal;
              const value1wAgo = historical.history['1w'] || actualTotal;
              const value1mAgo = historical.history['1m'] || actualTotal;

              // Calculate percentage changes based on historical values
              const change24h = value24hAgo > 0 ? ((actualTotal - value24hAgo) / value24hAgo) * 100 : 0;
              const change1w = value1wAgo > 0 ? ((actualTotal - value1wAgo) / value1wAgo) * 100 : 0;
              const change1m = value1mAgo > 0 ? ((actualTotal - value1mAgo) / value1mAgo) * 100 : 0;

              summaries[token.id] = {
                '24h': {
                  changePercent: parseFloat(change24h.toFixed(2)),
                  changeValue: parseFloat((actualTotal - value24hAgo).toFixed(2))
                },
                '1w': {
                  changePercent: parseFloat(change1w.toFixed(2)),
                  changeValue: parseFloat((actualTotal - value1wAgo).toFixed(2))
                },
                '1m': {
                  changePercent: parseFloat(change1m.toFixed(2)),
                  changeValue: parseFloat((actualTotal - value1mAgo).toFixed(2))
                }
              };

              // Update summary immediately for this token (progressive loading)
              setTokenSummaries(prev => ({ ...prev, [token.id]: summaries[token.id] }));
            } else {
              // Fallback to weighted average from asset changes
              const weightedChange = calculateWeightedChange(assetData.assets, actualTotal);
              summaries[token.id] = {
                '24h': {
                  changePercent: parseFloat(weightedChange.toFixed(2)),
                  changeValue: parseFloat((actualTotal * weightedChange / 100).toFixed(2))
                },
                '1w': {
                  changePercent: parseFloat((weightedChange * 3.5).toFixed(2)),
                  changeValue: parseFloat((actualTotal * weightedChange * 3.5 / 100).toFixed(2))
                },
                '1m': {
                  changePercent: parseFloat((weightedChange * 15).toFixed(2)),
                  changeValue: parseFloat((actualTotal * weightedChange * 15 / 100).toFixed(2))
                }
              };

              // Update summary immediately for this token (progressive loading)
              setTokenSummaries(prev => ({ ...prev, [token.id]: summaries[token.id] }));
            }

            // Cache the results
            tokenDataCache.set(cacheKey, {
              balance: actualTotal,
              summary: summaries[token.id],
              timestamp: Date.now()
            });
          } else {
            // Historical data failed, fallback to weighted average
            console.error(`Error fetching historical data for token ${token.id}:`, historicalResponse.reason);
            const weightedChange = calculateWeightedChange(assetData.assets, actualTotal);
            summaries[token.id] = {
              '24h': { 
                changePercent: parseFloat(weightedChange.toFixed(2)), 
                changeValue: parseFloat((actualTotal * weightedChange / 100).toFixed(2)) 
              },
              '1w': { changePercent: 0, changeValue: 0 },
              '1m': { changePercent: 0, changeValue: 0 }
            };
            
            // Update summary immediately for this token (progressive loading)
            setTokenSummaries(prev => ({ ...prev, [token.id]: summaries[token.id] }));
          }
        } else {
          console.error(`Error fetching asset data for token ${token.id}:`, assetResponse.reason);
          balances[token.id] = 0;
          summaries[token.id] = { '24h': { changePercent: 0, changeValue: 0 } };

          // Update with error state immediately
          setTokenBalances(prev => ({ ...prev, [token.id]: 0 }));
          setTokenSummaries(prev => ({ ...prev, [token.id]: { '24h': { changePercent: 0, changeValue: 0 } } }));
          setBalancesLoading(prev => ({ ...prev, [token.id]: false }));
        }

      } catch (error) {
        console.error(`Error processing token ${token.id}:`, error.message === 'Timeout' ? 'Request timeout' : error);
        balances[token.id] = 0;
        summaries[token.id] = { '24h': { changePercent: 0, changeValue: 0 } };

        // Update with error state immediately
        setTokenBalances(prev => ({ ...prev, [token.id]: 0 }));
        setTokenSummaries(prev => ({ ...prev, [token.id]: { '24h': { changePercent: 0, changeValue: 0 } } }));
        setBalancesLoading(prev => ({ ...prev, [token.id]: false }));
      }
    });
    
    // Wait for all tokens to be processed
    await Promise.all(tokenPromises);
    
    // Final update (in case any didn't get updated progressively)
    setTokenBalances(prev => ({ ...prev, ...balances }));
    setTokenSummaries(prev => ({ ...prev, ...summaries }));
    
    // Clear any remaining loading states
    setBalancesLoading({});
  };
  
  // Helper function to calculate weighted average change from asset data
  const calculateWeightedChange = (assets, totalValue) => {
    if (!assets || assets.length === 0 || totalValue === 0) return 0;
    
    let weightedChange = 0;
    assets.forEach(asset => {
      const weight = (parseFloat(asset.value) || 0) / totalValue;
      const change = parseFloat(asset.change24h) || 0;
      weightedChange += change * weight;
    });
    
    return weightedChange;
  };

  const fetchAvailableTokens = async () => {
    try {
      const response = await api.get('/available-tokens');
      const dbTokens = response.data.map(token => ({
        ...token,
        id: token.token_id,
        image: token.token_image,
        name: token.token_name,
        symbol: token.token_symbol,
        status: 'Available',
        marketCap: token.market_cap,
        minBalance: token.min_balance,
        mintAddress: token.mint_address
      }));
      
      setAvailableTokens(dbTokens);
    } catch (error) {
      console.error('Error fetching available tokens:', error);
      setAvailableTokens([]);
    }
  };

  const fetchAuthorizedTokens = async () => {
    try {
      const response = await api.get('/user/authorized-tokens');
      const mappedTokens = response.data.map(token => ({
        ...token,
        id: token.token_id || token.id,
        name: token.token_name || token.name,
        symbol: token.token_symbol || token.symbol,
        image: token.token_image || token.image,
        status: 'Active' // Set status for display
      }));
      setAuthorizedTokens(mappedTokens);
      setTokensLoaded(true); // Show tokens immediately
      setLoading(false); // Allow UI to render

      // Fetch wallet balances for the authorized tokens in parallel (async)
      if (mappedTokens.length > 0) {
        // Don't await - let balances load progressively
        fetchTokenBalances(mappedTokens);
      }
    } catch (error) {
      console.error('Error fetching authorized tokens:', error);
      // Set empty data if API fails
      setAuthorizedTokens([]);
      setTokenBalances({});
      setTokenSummaries({});
      setBalancesLoading({});
      setTokensLoaded(true);
      setLoading(false);
    }
  };

  const handleApplyForToken = async (tokenId) => {
    try {
      await api.post('/user/apply-token', { tokenId });
      alert('Application submitted successfully! You will be notified once approved.');
    } catch (error) {
      console.error('Error applying for token:', error);
      alert('Failed to submit application. Please try again.');
    }
  };

  const handleTokenClick = (token) => {
    // Navigate to dashboard with token context using path parameter
    navigate(`/dashboard/${token.id}`, { state: { tokenData: token } });
  };

  const handleEditImage = (e, token) => {
    e.stopPropagation(); // Prevent navigation to dashboard
    setEditingToken(token);
    setShowUpdateModal(true);
  };

  const handleImageUpdated = (updatedToken) => {
    // Update the token in the authorized tokens list
    setAuthorizedTokens(prev => 
      prev.map(t => t.token_id === updatedToken.token_id ? updatedToken : t)
    );
    setShowUpdateModal(false);
    setEditingToken(null);
    // Refresh the tokens list (this will also refresh balances)
    fetchAuthorizedTokens();
  };

  const renderTokenImage = (image) => {
    if (!image) return <span className="text-2xl">📊</span>;
    
    // Check if it's a URL (http/https or /token-images/)
    if (image.startsWith('http') || image.startsWith('https') || image.startsWith('/token-images/')) {
      const imageUrl = image.startsWith('/token-images/') 
        ? `${process.env.REACT_APP_API_URL}${image}`
        : image;
      
      return (
        <img 
          src={imageUrl} 
          alt="Token" 
          className="w-8 h-8 object-cover rounded"
          onError={(e) => {
            // Fallback to text display on error
            e.target.style.display = 'none';
            const fallback = e.target.nextSibling;
            if (fallback) fallback.style.display = 'inline';
          }}
        />
      );
    }
    
    // Display as emoji/icon
    return <span className="text-2xl">{image}</span>;
  };

  if (loading && !tokensLoaded) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50/30 to-blue-50/30 dark:from-purple-950/20 dark:to-blue-950/20">
      <main className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2">IP Token Market Making</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Select IP tokens to start market making or manage your existing positions
          </p>
        </div>

        {/* Available IP Tokens - Commented out for now
        <section className="mb-10">
          <h3 className="text-2xl font-semibold mb-6 flex items-center">
            <Bot className="w-6 h-6 mr-2" />
            Available IP Tokens
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {availableTokens.map((token) => (
              <div key={token.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="mr-2">
                      {renderTokenImage(token.image)}
                      <span style={{ display: 'none' }} className="text-2xl">{token.image}</span>
                    </div>
                    <h4 className="text-lg font-medium">{token.name}</h4>
                  </div>
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded">
                    {token.status}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 flex-grow">
                  {token.description}
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm mb-4">
                  <div>
                    <div className="text-gray-500 dark:text-gray-400">Market Cap</div>
                    <div className="font-semibold">${token.marketCap ? (token.marketCap / 1000000000).toFixed(1) : '0'}B</div>
                  </div>
                  <div>
                    <div className="text-gray-500 dark:text-gray-400">Holders</div>
                    <div className="font-semibold">{token.holders ? (token.holders / 1000).toFixed(1) : '0'}K</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-gray-500 dark:text-gray-400">Min Balance</div>
                    <div className="font-semibold">${token.minBalance ? token.minBalance.toLocaleString() : '0'}</div>
                  </div>
                </div>
                <button
                  onClick={() => handleApplyForToken(token.id)}
                  className="w-full bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-medium py-2 px-4 rounded transition-all"
                >
                  Apply
                </button>
              </div>
            ))}
          </div>
        </section>

        <div className="mb-10">
          <div className="border-t-2 border-gray-200 dark:border-gray-700"></div>
        </div>
        */}

        {/* Authorized IP Tokens */}
        <section>
          <h3 className="text-2xl font-semibold mb-6 flex items-center">
            <TrendingUp className="w-6 h-6 mr-2" />
            Authorized IP Tokens
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {authorizedTokens.map((token) => (
              <div
                key={token.id}
                className="bg-green-50 dark:bg-green-950 border-2 border-green-200 dark:border-green-800 rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => handleTokenClick(token)}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="relative group mr-2">
                      {renderTokenImage(token.image)}
                      <span style={{ display: 'none' }} className="text-2xl">{token.image}</span>
                      <button
                        onClick={(e) => handleEditImage(e, token)}
                        className="absolute -top-1 -right-1 p-1 bg-blue-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Edit image"
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                    </div>
                    <h4 className="text-lg font-medium">{token.name}</h4>
                  </div>
                  <span className="px-2 py-1 text-xs bg-green-600 text-white rounded">
                    {token.status}
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-3 text-sm mb-4">
                  <div>
                    <div className="text-gray-600 dark:text-gray-400">Total Portfolio Value</div>
                    <div className="font-semibold">
                      {balancesLoading[token.id] ? (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-purple-500"></div>
                          <span className="text-gray-400">Loading...</span>
                        </div>
                      ) : tokenBalances[token.id] !== undefined ? (
                        `$${parseFloat(tokenBalances[token.id]).toLocaleString(undefined, { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        })}`
                      ) : (
                        <span className="text-gray-400">$0.00</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-600 dark:text-gray-400">24h Change</div>
                    <div className={`font-semibold flex items-center ${
                      balancesLoading[token.id] 
                        ? 'text-gray-400' 
                        : (tokenSummaries[token.id]?.['24h']?.changePercent || 0) >= 0 
                          ? 'text-green-600' 
                          : 'text-red-600'
                    }`}>
                      {balancesLoading[token.id] ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-purple-500 mr-1"></div>
                          Loading...
                        </>
                      ) : (
                        <>
                          <TrendingUp className={`w-4 h-4 mr-1 ${
                            (tokenSummaries[token.id]?.['24h']?.changePercent || 0) < 0 ? 'rotate-180' : ''
                          }`} />
                          {tokenSummaries[token.id]?.['24h']?.changePercent !== undefined 
                            ? `${tokenSummaries[token.id]['24h'].changePercent > 0 ? '+' : ''}${tokenSummaries[token.id]['24h'].changePercent.toFixed(1)}%`
                            : '0.0%'
                          }
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end">
                  <button className="flex items-center text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium">
                    View Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* Update Image Modal */}
      {showUpdateModal && editingToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="max-w-md w-full mx-4">
            <UpdateTokenImage
              token={editingToken}
              onUpdate={handleImageUpdated}
              onClose={() => {
                setShowUpdateModal(false);
                setEditingToken(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default IPTokenSelection;